import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import useRealtimeSubscription from '../../../hooks/useRealtimeSubscription';

const FUNNEL_STAGES = [
  { key: 'impressions', label: 'Impressions', icon: 'Eye', color: 'bg-blue-500', light: 'bg-blue-50 text-blue-700 border-blue-200' },
  { key: 'clicks', label: 'Clicks', icon: 'MousePointer', color: 'bg-violet-500', light: 'bg-violet-50 text-violet-700 border-violet-200' },
  { key: 'purchases', label: 'Purchases', icon: 'ShoppingCart', color: 'bg-amber-500', light: 'bg-amber-50 text-amber-700 border-amber-200' },
  { key: 'verified', label: 'Verified', icon: 'CheckCircle', color: 'bg-emerald-500', light: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
];

const ConversionFunnelPanel = ({ brandId }) => {
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [campaigns, setCampaigns] = useState([]);
  const [funnelData, setFunnelData] = useState({ impressions: 0, clicks: 0, purchases: 0, verified: 0 });
  const [campaignFunnels, setCampaignFunnels] = useState([]);

  const loadFunnelData = useCallback(async () => {
    if (!brandId) return;
    try {
      // Load campaigns
      const { data: campData } = await supabase
        ?.from('campaigns')
        ?.select('id, title, status')
        ?.eq('brand_id', brandId)
        ?.in('status', ['active', 'completed', 'paused'])
        ?.order('created_at', { ascending: false });

      if (campData) setCampaigns(campData);

      // Load all transactions for this brand
      const query = supabase
        ?.from('transactions')
        ?.select('id, campaign_id, status, purchase_value, social_post_url, receipt_url, created_at')
        ?.eq('brand_id', brandId);

      if (selectedCampaign !== 'all') {
        query?.eq('campaign_id', selectedCampaign);
      }

      const { data: txData, error } = await query;

      if (!error && txData) {
        // Derive funnel stages from transaction data:
        // impressions = all submissions (any status)
        // clicks = submissions with social_post_url filled
        // purchases = submissions with purchase_value > 0
        // verified = approved transactions
        const impressions = txData?.length;
        const clicks = txData?.filter(t => t?.social_post_url && t?.social_post_url !== '')?.length;
        const purchases = txData?.filter(t => parseFloat(t?.purchase_value) > 0)?.length;
        const verified = txData?.filter(t => t?.status === 'approved')?.length;

        setFunnelData({ impressions, clicks, purchases, verified });

        // Per-campaign breakdown
        if (selectedCampaign === 'all' && campData?.length) {
          const perCampaign = campData?.map(c => {
            const ctxs = txData?.filter(t => t?.campaign_id === c?.id);
            return {
              id: c?.id,
              name: c?.title,
              status: c?.status,
              impressions: ctxs?.length,
              clicks: ctxs?.filter(t => t?.social_post_url && t?.social_post_url !== '')?.length,
              purchases: ctxs?.filter(t => parseFloat(t?.purchase_value) > 0)?.length,
              verified: ctxs?.filter(t => t?.status === 'approved')?.length,
              conversionRate: ctxs?.length > 0
                ? ((ctxs?.filter(t => t?.status === 'approved')?.length / ctxs?.length) * 100)?.toFixed(1)
                : '0.0',
            };
          });
          setCampaignFunnels(perCampaign);
        } else {
          setCampaignFunnels([]);
        }
      }
    } catch (err) {
      console.error('[ConversionFunnelPanel] error:', err);
    } finally {
      setLoading(false);
    }
  }, [brandId, selectedCampaign]);

  useEffect(() => {
    setLoading(true);
    loadFunnelData();
  }, [loadFunnelData]);

  useRealtimeSubscription({
    channelName: `brand-funnel-${brandId}`,
    table: 'transactions',
    filter: brandId ? `brand_id=eq.${brandId}` : undefined,
    enabled: !!brandId,
    onChange: () => loadFunnelData(),
  });

  const maxVal = Math.max(funnelData?.impressions, 1);

  const getDropRate = (from, to) => {
    if (!from) return '0';
    return (((from - to) / from) * 100)?.toFixed(0);
  };

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
            <Icon name="Filter" size={20} className="text-amber-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-foreground">Conversion Funnel</h3>
            <p className="text-xs text-muted-foreground">Impressions → Clicks → Purchases → Verified</p>
          </div>
        </div>
        <select
          value={selectedCampaign}
          onChange={e => setSelectedCampaign(e?.target?.value)}
          className="text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30">
          <option value="all">All Campaigns</option>
          {campaigns?.map(c => (
            <option key={c?.id} value={c?.id}>{c?.title}</option>
          ))}
        </select>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Funnel Visualization */}
          <div className="space-y-3 mb-6">
            {FUNNEL_STAGES?.map((stage, idx) => {
              const val = funnelData?.[stage?.key];
              const pct = maxVal > 0 ? (val / maxVal) * 100 : 0;
              const prevVal = idx > 0 ? funnelData?.[FUNNEL_STAGES?.[idx - 1]?.key] : null;
              const dropRate = prevVal !== null ? getDropRate(prevVal, val) : null;
              return (
                <div key={stage?.key}>
                  {dropRate !== null && (
                    <div className="flex items-center gap-2 py-1 pl-4">
                      <Icon name="ArrowDown" size={12} className="text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">{dropRate}% drop-off</span>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${stage?.light} border`}>
                      <Icon name={stage?.icon} size={14} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-foreground">{stage?.label}</span>
                        <span className="text-sm font-bold text-foreground">{val?.toLocaleString()}</span>
                      </div>
                      <div className="h-3 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-700 ${stage?.color}`}
                          style={{ width: `${Math.max(pct, val > 0 ? 2 : 0)}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground w-10 text-right">
                      {maxVal > 0 ? pct?.toFixed(0) : 0}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Overall conversion rate */}
          <div className="bg-muted/50 rounded-lg p-4 flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Icon name="Target" size={16} className="text-primary" />
              <span className="text-sm font-medium text-foreground">Overall Conversion Rate</span>
            </div>
            <span className="text-xl font-bold text-primary">
              {funnelData?.impressions > 0
                ? ((funnelData?.verified / funnelData?.impressions) * 100)?.toFixed(1)
                : '0.0'}%
            </span>
          </div>

          {/* Per-campaign table */}
          {campaignFunnels?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-3">Per Campaign Breakdown</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 pr-4 text-xs font-medium text-muted-foreground">Campaign</th>
                      <th className="text-right py-2 px-2 text-xs font-medium text-muted-foreground">Impr.</th>
                      <th className="text-right py-2 px-2 text-xs font-medium text-muted-foreground">Clicks</th>
                      <th className="text-right py-2 px-2 text-xs font-medium text-muted-foreground">Purchases</th>
                      <th className="text-right py-2 pl-2 text-xs font-medium text-muted-foreground">Conv. Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaignFunnels?.map(c => (
                      <tr key={c?.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                        <td className="py-2 pr-4">
                          <div className="flex items-center gap-2">
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                              c?.status === 'active' ? 'bg-emerald-500' : 'bg-muted-foreground'
                            }`} />
                            <span className="text-foreground truncate max-w-[120px]">{c?.name}</span>
                          </div>
                        </td>
                        <td className="text-right py-2 px-2 text-muted-foreground">{c?.impressions}</td>
                        <td className="text-right py-2 px-2 text-muted-foreground">{c?.clicks}</td>
                        <td className="text-right py-2 px-2 text-muted-foreground">{c?.purchases}</td>
                        <td className="text-right py-2 pl-2">
                          <span className={`font-semibold ${
                            parseFloat(c?.conversionRate) >= 50 ? 'text-emerald-600'
                            : parseFloat(c?.conversionRate) >= 20 ? 'text-amber-600' :'text-muted-foreground'
                          }`}>{c?.conversionRate}%</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ConversionFunnelPanel;
